import { useCallback, useState } from "react";
import { FaDownload } from "react-icons/fa6";
import { Button, Badge, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import useSecurityRoles from "../../hook/useSecurityRoles";
import useToggle from "../../hook/useToggle";
import { ICategory, IOrg, getOrgName } from "../../types/org";
import { ISearchUsersInput } from "../../types/user";
import { getFormDataObject } from "../../utils/commons";
import BusyButton from "../common/busy-button";
import { RefreshIcon, SearchIcon } from "../common/icons";
import OrgSelectionModal from "./org-selection-modal";
import './style.scss';

interface UserSearchFormProps {
  onSubmit: (input: ISearchUsersInput) => void;
  onReset?: () => void;
  onExport?: () => void;
  busy?: boolean;
  hasData?: boolean;
  admin?: boolean;
}

const UserSearchForm = (props: UserSearchFormProps) => {
  const { onSubmit, onReset, onExport, busy, hasData, admin } = props;

  const [securityRoles] = useSecurityRoles();

  const { isOpen: isModalOpen, toggle: toggleModal } = useToggle();

  const [selectedOrg, setSelectedOrg] = useState<ICategory | IOrg>();
  const [accountStatus, setAccountStatus] = useState(1);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const input = getFormDataObject<ISearchUsersInput>(event.currentTarget);
    if (input.status) {
      input.status = +input.status;
    }

    onSubmit(input);
  };

  const handleReset = () => {
    setSelectedOrg(undefined);
    setAccountStatus(1);

    onReset?.();
  };

  const handleChangeStatus = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    const status = checked ? (accountStatus | +value) : (accountStatus & ~+value);
    setAccountStatus(status || (+value === 1 ? 2 : 1));
  }, [accountStatus]);

  return (
    <Form onSubmit={handleSubmit} onReset={handleReset} id="user_search_form">
      <Row>
        {admin &&
          <Col md={6}>
            <FormGroup row>
              <Label for="role_type" sm={4}>Filter by Admin Role:</Label>
              <Col sm={6}>
                <Input type="select" id="role_type" name="roleTypeId">
                  <option value="0">All</option>
                  {securityRoles?.map((role, index) =>
                    <option key={role.id} value={role.id} title={role.desc}>{role.desc}</option>
                  )}
                </Input>
              </Col>
            </FormGroup>
          </Col>
        }
        <Col md={admin ? 6 : 12}>
          <FormGroup row>
            <Label for="org" sm={admin ? 4 : 2}>Search by Organization:</Label>
            <Col sm={admin ? 6 : 10}>
              <Input type="hidden" name="orgId" value={selectedOrg?.id ?? 0} />

              <Button color="primary" outline className="me-3" onClick={toggleModal}>
                <SearchIcon /> Search
              </Button>
              <Badge color="secondary" className="text-dark">{getOrgName(selectedOrg) ?? 'All'}</Badge>

              <OrgSelectionModal isOpen={isModalOpen} supportAll={true} toggle={toggleModal}
                onSelect={setSelectedOrg} />
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <FormGroup row>
            <Label for="first_name" sm={4}>First Name:</Label>
            <Col sm={6}>
              <Input id="first_name" name="firstName" placeholder="Enter a user's first name" />
            </Col>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup row>
            <Label for="last_name" sm={4}>Last Name:</Label>
            <Col sm={6}>
              <Input id="last_name" name="lastName" placeholder="Enter a user's last name" />
            </Col>
          </FormGroup>
        </Col>
      </Row>

      <Row className="pt-2">
        <Col className="col-auto oms-row">
          <BusyButton type="submit" color="primary" className="action-link"
            value="Search" busy={busy}>
            <SearchIcon />
          </BusyButton>

          <Button type="reset" color="secondary" className="action-link" disabled={busy}>
            <RefreshIcon /> Reset
          </Button>

          <Button color="secondary" aria-label="Export to Excel file"
            onClick={onExport} disabled={busy || !hasData}>
            <FaDownload /> Export
          </Button>
        </Col>

        {admin && <>
          <Input type="hidden" name="status" value={accountStatus} />

          <Col className="col-auto pt-2 ps-5">
            <FormGroup check>
              <Input id="show_active" type="checkbox" value="1"
                checked={(accountStatus & 1) !== 0} onChange={handleChangeStatus} />
              {' '}
              <Label for="show_active" check>Show active</Label>
            </FormGroup>
          </Col>
          <Col className="pt-2 ps-3">
            <FormGroup check>
              <Input id="show_inactive" type="checkbox" value="2"
                checked={(accountStatus & 2) !== 0} onChange={handleChangeStatus} />
              {' '}
              <Label for="show_inactive" check>Show inactive</Label>
            </FormGroup>
          </Col>
        </>}
      </Row>
    </Form>
  );
};

export default UserSearchForm;

